import type { Scene } from 'phaser';
import { GameObjects } from 'phaser';
import type { Player } from 'hearts-engine';

import Button, { BtnEvent } from '@/GameUI/Components/Button';
import { getResolution } from '@/GameUI/Constants';
import { HALF_SCREEN_HEIGHT, HALF_SCREEN_WIDTH } from '@/utilities/Screen';
import type { TFunction } from '@/GameUI/Definitions';
import type { GameEngine } from '@/index';

const { Container, Graphics } = GameObjects;

export enum RoundScoreBoardEvent {
	CLOSE_BOARD = 'CLOSE_BOARD',
}

const WIDTH = 500;
const HEIGHT = 730;

const LAYOUT = {
	btnWidth: 420,
	btnHeight: 150,
	btnBottomPadding: 100,
	titleX: 0,
	titleY: 5,
	gamePointX: 0,
	gamePointY: 60,
	gameResultX: 0,
	gameResultY: -65,
};

const TEXT_STYLE = {
	align: 'center',
	color: '#FFF',
	fontFamily: 'Arial',
	fontSize: 26 * 2,
	resolution: getResolution(),
	padding: {
		x: 10,
		y: 10,
	},
};

const TITLE_STYLE = {
	...TEXT_STYLE,
	fixedWidth: WIDTH,
};

const INSTRUCTIONS_STYLE = {
	...TITLE_STYLE,
	fontSize: 30,
};

const SCORE_STYLE = {
	...TEXT_STYLE,
	fontSize: 40,
};

const SIDE_PADDING = 100;

const SCORE_VERTICAL_PADDING = 100;
const SCORE_START_Y = 200;
const BOTTOM_Y = SCORE_START_Y + 250;

const LAYOUTS = {
	Top: [WIDTH / 2, SCORE_START_Y],
	Right: [WIDTH - SIDE_PADDING, SCORE_START_Y + SCORE_VERTICAL_PADDING],
	Bottom: [WIDTH / 2, BOTTOM_Y],
	Left: [SIDE_PADDING, SCORE_START_Y + SCORE_VERTICAL_PADDING],
};

const DEFAULT_INSTRUCTIONS = 'Round Results';
const DEFAULT_BUTTON_TEXT = 'Continue';

class RoundScoreBoard extends Container {
	private playerEntries: GameObjects.Text[] = [];
	private button: Button = null;
	private players: Player[];
	private background: GameObjects.Graphics;
	private instructions: GameObjects.Text;
	private t: TFunction;

	constructor(scene: Scene, pointTotal: number, players: Player[], translate: TFunction, positionMap: GameEngine.PlayerPosition) {
		super(scene, HALF_SCREEN_WIDTH - (WIDTH / 2), HALF_SCREEN_HEIGHT - (HEIGHT / 2));
		this.players = players;
		this.width = WIDTH;
		this.height = HEIGHT;
		this.t = translate;
		const graphics = new Graphics(scene);
		graphics.fillStyle(0x479ACE);
		graphics.lineStyle(0, 0xFFFFFF, 1);
		graphics.fillRoundedRect(0, 0, WIDTH, HEIGHT, 10);
		graphics.setDepth(10);
		this.background = graphics;
		this.add(this.background);

		this.instructions = new GameObjects.Text(scene, LAYOUT.titleX, LAYOUT.titleY, this.t(DEFAULT_INSTRUCTIONS), TITLE_STYLE);
		this.add(this.instructions);

		const gameTotal = new GameObjects.Text(scene, LAYOUT.gamePointX, LAYOUT.gamePointY, this.t('{{pointTotal}} point game', {
			pointTotal,
		}), INSTRUCTIONS_STYLE);
		this.add(gameTotal);

		this.button = new Button(this.t(DEFAULT_BUTTON_TEXT), 0xC03500, LAYOUT.btnWidth, LAYOUT.btnHeight, null, this.scene, WIDTH / 2, HEIGHT - LAYOUT.btnBottomPadding);
		this.button.on(BtnEvent.PRESSED, () => {
			this.handleOnClick();
		});
		this.add(this.button);

		for (const player of this.players) {
			const layout = LAYOUTS[positionMap[player.position]];
			const playerEntry = new GameObjects.Text(scene, layout[0], layout[1], '', SCORE_STYLE);
			playerEntry.setFixedSize(400, 0);
			playerEntry.setOrigin(.5, .5);
			this.playerEntries.push(playerEntry);
			this.add(playerEntry);
		}
	}

	handleOnClick() {
		this.emit(RoundScoreBoardEvent.CLOSE_BOARD);
	}

	setScore(score: { total: number[], round: number[] }) {
		for (let x = 0; x < score.round.length; ++x) {
			const roundScore = score.round[x];
			const totalScore = score.total[x];
			const player = this.players[x];

			if (typeof this.playerEntries[x] !== 'undefined') {
				const nameObject = this.playerEntries[x];
				nameObject.setText(`${this.chopName(player.name)}\n+${roundScore ?? 0}\n(${totalScore ?? 0})`);
			}
		}
	}

	setInstructions(text: string) {
		this.instructions.setText(text);
	}

	setButtonText(text: string) {
		this.button.setMessage(text);
	}

	setPlayers(players: Player[]) {
		this.players = players;
	}

	reset() {
		this.setInstructions(this.t(DEFAULT_INSTRUCTIONS));
		this.setButtonText(this.t(DEFAULT_BUTTON_TEXT));
	}

	private chopName(name: string): string {
		return name.substring(0, 10);
	}
}

export default RoundScoreBoard;
